/**
 * Estado de conexão derivado (§10.6.1, dona da função e das regras). Nenhum componente escreve `ConnectionState`:
 * o `FeedClient` (e o `app/feedBinding.ts`, quando `timeline.live` muda) chama esta função a cada mudança das
 * entradas e a cada 15 s, para o limite de 5 min valer mesmo sem respostas novas.
 *
 * Prioridade: replay > offline > connecting > reconnecting > tse_stalled > stalled > live/simulation.
 * `stalled` e `tse_stalled` nunca disparam com a apuração concluída em 100% (`counting = false`). A 0%, só `stalled` e
 * só a partir de 17h10 (`zeroLate`, pré-mortem de 25/10, ORD-F-148 M3): antes, é a espera do 1º boletim do TSE; depois,
 * a contagem parada a 0% é falha do caminho do dado.
 *
 * `lastChangeAt` é a hora do dado (o `published_at` do último `seq` novo, no relógio da fonte; nunca no futuro), e não a
 * da chegada: quem abre a página com o feed velho já vê "Sem números novos" (ORD-F-147).
 */
import type { Ops } from '../contracts/snapshot';
import type { ConnectionState } from '../model/types';

export interface ConnectionInputs {
  timelineLive: boolean;
  mode: 'live' | 'simulation';
  /** navigator.onLine + eventos online/offline. */
  online: boolean;
  /** Existe snapshot vivo aceito. */
  hasData: boolean;
  /** feed.failures > 0. */
  lastFetchFailed: boolean;
  /** Brasil: 0 < sections_counted < sections. */
  counting: boolean;
  /** Brasil a 0% depois de 17h10 (T_OPEN + 10 min) no dia da eleição (ORD-F-148 M3). Padrão: false. */
  zeroLate?: boolean;
  nowMs: number;
  /** feed.lastChangeAt: hora do dado do último seq novo aceito (ORD-F-147). */
  lastChangeAt: number | null;
  /** clocks.tse_ht do último snapshot aceito. */
  tseHtMs: number | null;
  /** clocks.verified_at mais recente visto (mesmo com seq igual). */
  verifiedAtMs: number | null;
  /** Do último snapshot aceito; null antes do primeiro. */
  sourceStatus: Ops['source_status'] | null;
}

/** 5 min sem `seq` novo (ou com `tse_ht` parado) → alerta. */
export const STALL_MS = 5 * 60_000;
/** Verificação no TSE "recente": até 120 s. */
export const VERIFY_FRESH_MS = 120_000;

export function deriveConnection(i: ConnectionInputs): ConnectionState {
  if (!i.timelineLive) return 'replay';
  if (!i.online) return 'offline';
  if (!i.hasData) return 'connecting';
  if (i.lastFetchFailed) return 'reconnecting';
  if (i.counting) {
    const tseQuiet =
      i.sourceStatus === 'tse_stalled' ||
      (i.tseHtMs !== null &&
        i.nowMs - i.tseHtMs > STALL_MS &&
        i.verifiedAtMs !== null &&
        i.nowMs - i.verifiedAtMs <= VERIFY_FRESH_MS);
    if (tseQuiet) return 'tse_stalled';
    if (i.sourceStatus === 'tse_unreachable' || i.sourceStatus === 'maintenance') return 'stalled';
    if (i.lastChangeAt !== null && i.nowMs - i.lastChangeAt > STALL_MS) return 'stalled';
  } else if (i.zeroLate && i.lastChangeAt !== null && i.nowMs - i.lastChangeAt > STALL_MS) {
    return 'stalled';
  }
  return i.mode === 'simulation' ? 'simulation' : 'live';
}
